import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOffIcon from '@mui/icons-material/EditOff';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SlideExportButton } from '../components/slides/SlideExportButton';
import { SlidePreviewDialog } from '../components/slides/SlidePreviewDialog';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { lessonService } from '../services/lessonService';
import { imageService } from '../services/imageService';
import { useLessonStore } from '../store/lessonStore';
import { useEditorStore } from '../store/editorStore';
import { useEditLock } from '../hooks/useEditLock';
import { PhaseIndicator } from '../components/common/PhaseIndicator';
import { AutoSaveIndicator } from '../components/common/AutoSaveIndicator';
import { ValidationDrawer } from '../components/validation/ValidationDrawer';
import { Phase0Chat } from './phases/Phase0Chat';
import { Phase1Planning } from './phases/Phase1Planning';
import { Phase2Slides } from './phases/Phase2Slides';
import { Phase3Images } from './phases/Phase3Images';
import { Phase4Assembly } from './phases/Phase4Assembly';
import { useValidationStore } from '../store/validationStore';
import type { IntakeSummary, SlidesHTML } from '../types';

const PHASE_NAMES = ['Intake', 'Plan', 'Slides', 'Images', 'Export'];

export const LessonEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMarkdown, setPreviewMarkdown] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Back-navigation state
  const [phaseNavDialog, setPhaseNavDialog] = useState<{ targetPhase: 0 | 1 | 2 | 3 | 4 } | null>(null);
  const [isReviewMode, setIsReviewMode] = useState(false);
  // The DB phase to return to when leaving review mode
  const [returnPhase, setReturnPhase] = useState<0 | 1 | 2 | 3 | 4>(0);

  const { setCurrentLesson, setCurrentVersion, updateSlidesHTML, reset: resetLesson } = useLessonStore();
  const {
    currentPhase,
    setPhase,
    isSaving,
    lastSaved,
    reset: resetEditor,
  } = useEditorStore();

  const { isMyLock, lockedBy, showLockedModal, dismissLockedModal } = useEditLock(id ?? '');
  const { drawerOpen, openDrawer, closeDrawer, status: validationStatus } = useValidationStore();

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['lesson', id],
    queryFn: () => lessonService.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (data) {
      setCurrentLesson(data.lesson);
      setCurrentVersion(data.version);
      const savedPhase = data.version.phase;
      if (savedPhase >= 0 && savedPhase <= 4) {
        setPhase(savedPhase as 0 | 1 | 2 | 3 | 4);
      }
    }
  }, [data, setCurrentLesson, setCurrentVersion, setPhase]);

  useEffect(() => {
    return () => {
      resetLesson();
      resetEditor();
    };
  }, [resetLesson, resetEditor]);

  // Clear all data generated in phases after `fromPhase`.
  const clearDownstreamFrom = async (fromPhase: number) => {
    if (!id || !data?.version) return;
    const v = data.version.version_number;
    if (fromPhase <= 1) {
      await lessonService.saveVersion(id, v, { slides_html: {} as unknown as SlidesHTML });
      updateSlidesHTML({} as unknown as SlidesHTML);
    }
    if (fromPhase <= 2) {
      await imageService.clearImages(id, v);
      queryClient.removeQueries({ queryKey: ['images', id, v] });
    }
  };

  const handlePhaseClick = (phase: 0 | 1 | 2 | 3 | 4) => {
    if (phase < currentPhase || (isReviewMode && phase !== currentPhase)) {
      setPhaseNavDialog({ targetPhase: phase });
    }
  };

  const handlePhaseNavReview = () => {
    if (!phaseNavDialog) return;
    setReturnPhase(currentPhase);
    setPhase(phaseNavDialog.targetPhase);
    setIsReviewMode(true);
    setPhaseNavDialog(null);
  };

  const handlePhaseNavEdit = async () => {
    if (!phaseNavDialog) return;
    const target = phaseNavDialog.targetPhase;
    setPhaseNavDialog(null);
    await clearDownstreamFrom(target);
    setPhase(target);
    setIsReviewMode(false);
  };

  const handleReturnFromReview = () => {
    setPhase(returnPhase);
    setIsReviewMode(false);
  };

  const handleIntakeApprove = async (summary: IntakeSummary) => {
    if (!id || !data?.version) return;

    await lessonService.update(id, {
      title: summary.title,
      cefr_level: summary.cefr_level,
      topic: summary.topic,
      duration_min: summary.duration_min,
    });

    // Build lesson plan skeleton from Phase 0 stages (names/aims locked; procedure empty).
    const lessonPlan = summary.stages && summary.stages.length > 0
      ? {
          stages: summary.stages.map((s) => ({
            name: s.name,
            duration_min: s.duration_min,
            aim: s.aim,
            suggested_slides: s.suggested_slides,
            procedure: '',
            interaction: '',
            teacher_notes: '',
            extension: '',
          })),
        }
      : undefined;

    // Build slide mapping from Phase 0 stages.
    const slideMapping: Record<string, number> = {};
    summary.stages?.forEach((s) => {
      slideMapping[s.name] = s.suggested_slides ?? 2;
    });

    await lessonService.saveVersion(id, data.version.version_number, {
      base_prompt: summary.base_prompt,
      lesson_plan: lessonPlan,
      slide_mapping: Object.keys(slideMapping).length > 0 ? slideMapping : undefined,
      phase: 1,
    });

    await queryClient.invalidateQueries({ queryKey: ['lesson', id] });
    setPhase(1);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          }
        >
          Failed to load lesson:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  const lesson = data?.lesson;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* ── Main column ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* ── Combined header bar ── */}
        {(() => {
          const CEFR_COLORS: Record<string, string> = {
            A1: '#4caf50', A2: '#8bc34a',
            B1: '#2196f3', B2: '#1565c0',
            C1: '#9c27b0', C2: '#6a1b9a',
          };
          return (
            <Box
              sx={{
                px: 2,
                height: 52,
                flexShrink: 0,
                borderBottom: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex', alignItems: 'center', gap: 1.5,
                overflow: 'hidden',
              }}
            >
              {/* Left: lesson title + chips */}
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  flexShrink: 0,
                  maxWidth: 400,
                  overflow: 'hidden',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  noWrap
                  sx={{ maxWidth: 240, fontSize: '0.88rem' }}
                >
                  {lesson?.title ?? 'Loading…'}
                </Typography>
                {lesson?.cefr_level && (
                  <Chip
                    label={lesson.cefr_level}
                    size="small"
                    sx={{
                      height: 18, fontSize: '0.6rem', fontWeight: 700,
                      bgcolor: CEFR_COLORS[lesson.cefr_level] ?? 'grey.500',
                      color: 'white', flexShrink: 0,
                    }}
                  />
                )}
                {lesson?.duration_min && (
                  <Chip
                    label={`${lesson.duration_min}m`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
                  />
                )}
              </Box>

              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Centre: compact phase tab-strip */}
              <PhaseIndicator currentPhase={currentPhase} onPhaseClick={handlePhaseClick} />

              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Right: action buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                {!isMyLock && lockedBy && (
                  <Alert severity="warning" icon={<LockIcon />} sx={{ py: 0, fontSize: 11, px: 1 }}>
                    Read-only
                  </Alert>
                )}
                <AutoSaveIndicator isSaving={isSaving} lastSaved={lastSaved} hasError={false} />
                {id && data?.version && (
                  <Box sx={{ display: 'none' }}>
                    <SlideExportButton
                      lessonId={id}
                      versionNumber={data.version.version_number}
                      onHTMLReady={(html, markdown) => {
                        setPreviewHtml(html);
                        setPreviewMarkdown(markdown);
                        setPreviewOpen(true);
                      }}
                    />
                  </Box>
                )}
                <Tooltip title={drawerOpen ? 'Hide quality panel' : 'Show quality panel'}>
                  <IconButton
                    size="small"
                    onClick={() => (drawerOpen ? closeDrawer() : openDrawer())}
                    color={
                      validationStatus === 'running'   ? 'error'   :
                      validationStatus === 'complete'  ? 'success' : 'default'
                    }
                  >
                    <VerifiedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button variant="outlined" size="small" onClick={() => navigate('/dashboard')}>
                  Exit
                </Button>
              </Box>
            </Box>
          );
        })()}

        {/* Phase content */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Review mode banner */}
          {isReviewMode && (
            <Box sx={{
              px: 2, py: 0.75, flexShrink: 0,
              bgcolor: 'rgba(255,167,38,0.10)', borderBottom: '1px solid', borderColor: 'warning.light',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <EditOffIcon sx={{ fontSize: 16, color: 'warning.dark' }} />
              <Typography variant="caption" color="warning.dark" fontWeight={600} sx={{ flex: 1 }}>
                Review mode — viewing {PHASE_NAMES[currentPhase]}. Changes are disabled.
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                onClick={handleReturnFromReview}
                sx={{ py: 0.25, fontSize: '0.72rem', borderRadius: 1.5 }}
              >
                Return to {PHASE_NAMES[returnPhase]}
              </Button>
            </Box>
          )}

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {id && (() => {
              const ro = (!isMyLock && !!lockedBy) || isReviewMode;
              return (
                <>
                  {currentPhase === 0 && (
                    <Phase0Chat
                      lessonId={id}
                      initialHistory={data?.version?.intake_chat_history ?? []}
                      initialSummary={data?.version?.intake_summary}
                      onApprove={handleIntakeApprove}
                      readOnly={ro}
                    />
                  )}
                  {currentPhase === 1 && <Phase1Planning lessonId={id} readOnly={ro} />}
                  {currentPhase === 2 && <Phase2Slides lessonId={id} readOnly={ro} />}
                  {currentPhase === 3 && <Phase3Images lessonId={id} readOnly={ro} />}
                  {currentPhase === 4 && <Phase4Assembly lessonId={id} readOnly={ro} />}
                </>
              );
            })()}
          </Box>
        </Box>
      </Box>

      {/* ── Validation drawer ── */}
      <ValidationDrawer />

      {/* Slide preview dialog */}
      {id && data?.version && previewOpen && (
        <SlidePreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          html={previewHtml}
          markdown={previewMarkdown}
          lessonId={id}
          versionNumber={data.version.version_number}
          onMarkdownUpdated={(md, html) => {
            setPreviewMarkdown(md);
            setPreviewHtml(html);
          }}
        />
      )}

      {/* Phase back-navigation dialog */}
      <Dialog
        open={!!phaseNavDialog}
        onClose={() => setPhaseNavDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Go back to {phaseNavDialog ? PHASE_NAMES[phaseNavDialog.targetPhase] : ''}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>Review only</strong> — view the previous phase without making changes.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            <strong>Edit</strong> — go back and make changes. All slides, images, and exported
            content created in later phases will be permanently discarded and must be regenerated.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
          <Button onClick={() => setPhaseNavDialog(null)} sx={{ mr: 'auto' }}>
            Cancel
          </Button>
          <Button variant="outlined" startIcon={<EditOffIcon />} onClick={handlePhaseNavReview}>
            Review only
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<WarningAmberIcon />}
            onClick={() => void handlePhaseNavEdit()}
          >
            Edit (clear later work)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Locked by another user modal */}
      <Dialog open={showLockedModal} onClose={dismissLockedModal}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="warning" />
          Lesson is Locked
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This lesson is currently being edited by another user (ID: {lockedBy}). You can
            view it in read-only mode or wait for them to finish.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
          <Button variant="contained" onClick={dismissLockedModal}>
            View Read-Only
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
